import { useEffect, useRef, useState } from "react";
import { api } from "./api.ts";
import { distanceM } from "./geo.ts";
import type { Fix } from "./map/common.ts";

// Where this device is, while asked: the browser's location, watched (it asks the person first).
// Shown only on this screen; sent to the trip only while the person shares, and not more often than
// a move of 25 m or a minute.
export function useWhereIAm(on: boolean): { fix: Fix | null; error: string | null } {
  const [fix, setFix] = useState<Fix | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!on) {
      setFix(null);
      setError(null);
      return;
    }
    if (!("geolocation" in navigator)) {
      setError("This browser can't tell where you are.");
      return;
    }
    const watch = navigator.geolocation.watchPosition(
      (p) => {
        setError(null);
        setFix({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy });
      },
      (e) =>
        setError(
          e.code === e.PERMISSION_DENIED
            ? "Location is blocked for this site. Allow it in the browser to see where you are."
            : "Can't find where you are right now.",
        ),
      { enableHighAccuracy: true, maximumAge: 15_000, timeout: 20_000 },
    );
    return () => navigator.geolocation.clearWatch(watch);
  }, [on]);
  return { fix, error };
}

const MOVE_M = 25;
const EVERY_MS = 60_000;

export function useShareWhereIAm(trip: string, fix: Fix | null, sharing: boolean) {
  const last = useRef<{ fix: Fix; at: number } | null>(null);
  useEffect(() => {
    if (!sharing || !fix) return;
    const prev = last.current;
    const moved = prev ? distanceM([prev.fix.lng, prev.fix.lat], [fix.lng, fix.lat]) : Number.POSITIVE_INFINITY;
    if (prev && moved < MOVE_M && Date.now() - prev.at < EVERY_MS) return;
    last.current = { fix, at: Date.now() };
    api
      .sendLocation(trip, {
        lat: fix.lat,
        lng: fix.lng,
        ...(fix.accuracy !== undefined ? { accuracy_m: Math.round(fix.accuracy) } : {}),
      })
      .catch(() => {
        // Outside the trip's window or no longer sharing: the next fix tries again.
        last.current = null;
      });
  }, [trip, fix, sharing]);
}
